import React, { useContext, useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { useNavigate } from 'react-router-dom';
import { useAppTrackLocations } from '../../../hooks/useAppTrackLocation';
import Loading from '../../../components/Loading';
import { Heading } from '../../../components/Heading';
import { useAppUser } from '../../../hooks/useAppUser';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import Pagination from '../../../components/Pagination';
import dateFormat from 'dateformat';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { UserContext } from '../../../context/userContext';

const COLUMNS = [
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { key: 'created', label: 'Created On' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const AppUserPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchUsersPaginated, appUsers, deleteUser, total } = useAppUser();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const perPage = 10;
    const navigate = useNavigate();
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterWarehouseId, setFilterWarehouseId] = useState('');

    useEffect(() => {
        document.title = "App Users";
    }, []);

    useEffect(() => {
        // initial fetch
        fetchUsersPaginated(currentPage, perPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchUsersPaginated(currentPage, perPage, searchQuery || undefined, filterWarehouseId || undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchQuery, filterWarehouseId]);

    const resetFilters = () => {
        setFilterWarehouseId('');
    };

    const activeFilterCount = [filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    const handleOpenDeleteDialog = (userId: string) => {
        setSelectedUserId(userId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedUserId) return;

        setIsDeleting(true);
        try {
            await deleteUser(selectedUserId);
            fetchUsersPaginated(currentPage, perPage, searchQuery || undefined);
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete user:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>App Users</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => navigate('/gp/app/users/add')}><PlusIcon />Create new app user</Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search app user…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'username') && <TableHeader>Username</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'email') && <TableHeader>Email</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created On</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {appUsers.length > 0 ? [...appUsers].sort((a, b) => {
                                    const diff = new Date(a.created_time || 0).getTime() - new Date(b.created_time || 0).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((user, index) => (
                                    <TableRow key={user.id}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'username') && <TableCell>{user.username}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'email') && <TableCell>{user.email}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell>{user.created_time ? dateFormat(user.created_time, "mmmm dd, yyyy") : ''}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/app/users/edit/${user.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/app/users/view/${user.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(user.id.toString())}>Delete</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8">No app user found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={Math.ceil(total / perPage)}
                            onPageChange={setCurrentPage}
                            totalResult={total}
                            dataName={'Users'}
                        />
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                {(currentUser?.role?.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete App User</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this app user? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default AppUserPage;
